"use client"

import { usePathname } from "next/navigation"
import { CTA } from "@/components/sections/CTA"
import { type Locale } from "@/lib/i18n"

export default function PrivacyPolicyPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  
  return (
    <>
      <section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
              Gizlilik Politikası
            </h1>
            <p className="text-lg text-white/90 sm:text-xl">
              Son güncelleme: {new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </section>

      <section className="pt-12 pb-12 lg:pb-16 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Content */}
            <div className="prose prose-slate max-w-none space-y-6 sm:space-y-8 text-sm sm:text-base">
              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">1. Genel Bilgiler</h2>
                <p>
                  LAK VERSE olarak, kişisel verilerinizin korunmasına büyük önem vermekteyiz. Bu Gizlilik Politikası, 
                  web sitemizi ziyaret ettiğinizde veya hizmetlerimizden yararlandığınızda toplanan kişisel verilerinizin 
                  nasıl toplandığını, kullanıldığını, korunduğunu ve paylaşıldığını açıklamaktadır.
                </p>
                <p>
                  Bu politika, 6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") ve ilgili mevzuat hükümlerine 
                  uygun olarak hazırlanmıştır.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">2. Veri Sorumlusu</h2>
                <p>
                  Kişisel verilerinizin işlenmesinden sorumlu olan veri sorumlusu:
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm sm:text-base">
                  <p className="font-semibold mb-2">LAK VERSE</p>
                  <p className="text-sm text-slate-600">
                    E-posta: info@lakverse.com<br />
                    Web: www.lakverse.com
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">3. Toplanan Kişisel Veriler</h2>
                <p>Hizmetlerimiz kapsamında aşağıdaki kişisel verileriniz toplanabilmektedir:</p>
                
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mt-4 sm:mt-6 mb-2 sm:mb-3">3.1. Kimlik Bilgileri</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Ad, soyad</li>
                  <li>E-posta adresi</li>
                  <li>Telefon numarası</li>
                  <li>İş unvanı</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">3.2. İletişim Bilgileri</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>E-posta adresi</li>
                  <li>Telefon numarası</li>
                  <li>Adres bilgileri</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">3.3. İşlem Bilgileri</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Proje talepleri ve detayları</li>
                  <li>Teklif formu bilgileri</li>
                  <li>İletişim formu bilgileri</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">3.4. Teknik Bilgiler</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>IP adresi</li>
                  <li>Tarayıcı bilgileri</li>
                  <li>Çerez (cookie) bilgileri</li>
                  <li>Ziyaret edilen sayfalar</li>
                  <li>Ziyaret tarih ve saati</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">4. Kişisel Verilerin İşlenme Amaçları</h2>
                <p>Toplanan kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:</p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Hizmet taleplerinizin değerlendirilmesi ve karşılanması</li>
                  <li>Teklif hazırlama ve sunma</li>
                  <li>Proje yönetimi ve takibi</li>
                  <li>Müşteri ilişkileri yönetimi</li>
                  <li>İletişim ve bilgilendirme faaliyetleri</li>
                  <li>Yasal yükümlülüklerin yerine getirilmesi</li>
                  <li>Web sitesi analizi ve iyileştirme</li>
                  <li>Güvenlik ve dolandırıcılık önleme</li>
                  <li>Pazarlama ve tanıtım faaliyetleri (açık rıza ile)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">5. Kişisel Verilerin İşlenme Hukuki Sebepleri</h2>
                <p>Kişisel verileriniz aşağıdaki hukuki sebeplerle işlenmektedir:</p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Açık rızanız</li>
                  <li>Sözleşmenin kurulması veya ifası ile doğrudan ilgili olması</li>
                  <li>Yasal yükümlülüğümüzün yerine getirilmesi</li>
                  <li>Hukuki yükümlülüklerimizin yerine getirilmesi</li>
                  <li>Meşru menfaatlerimiz (hizmet kalitesinin artırılması, güvenliğin sağlanması)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">6. Kişisel Verilerin Paylaşılması</h2>
                <p>
                  Kişisel verileriniz, yukarıda belirtilen amaçların gerçekleştirilmesi için aşağıdaki üçüncü kişilerle 
                  paylaşılabilir:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Yasal yükümlülüklerimiz gereği kamu kurum ve kuruluşları</li>
                  <li>Hizmet sağlayıcılarımız (hosting, e-posta servisleri, analitik araçlar)</li>
                  <li>İş ortaklarımız ve tedarikçilerimiz</li>
                  <li>Yasal danışmanlarımız ve muhasebe firmaları</li>
                </ul>
                <p className="mt-4">
                  Kişisel verileriniz, yasal zorunluluklar dışında üçüncü kişilerle paylaşılmaz.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">7. Çerezler (Cookies)</h2>
                <p>
                  Web sitemizde, kullanıcı deneyimini iyileştirmek ve site performansını analiz etmek amacıyla çerezler 
                  kullanılmaktadır. Çerez kullanımı hakkında detaylı bilgi için{" "}
                  <a href="/kvkk" className="text-blue-600 hover:underline">KVKK Aydınlatma Metni</a> sayfamızı ziyaret edebilirsiniz.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">8. Kişisel Verilerin Saklanma Süresi</h2>
                <p>
                  Kişisel verileriniz, işleme amacının gerektirdiği süre boyunca ve yasal saklama süreleri dahilinde 
                  saklanmaktadır. Bu süre sona erdiğinde, kişisel verileriniz yasalara uygun şekilde silinir, yok edilir 
                  veya anonim hale getirilir.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">9. Kişisel Verilerinizin Güvenliği</h2>
                <p>
                  Kişisel verilerinizin güvenliğini sağlamak için teknik ve idari güvenlik önlemleri almaktayız:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>SSL/TLS şifreleme teknolojileri</li>
                  <li>Güvenli sunucu altyapısı</li>
                  <li>Düzenli güvenlik güncellemeleri</li>
                  <li>Erişim kontrolü ve yetkilendirme</li>
                  <li>Düzenli yedekleme ve veri kurtarma prosedürleri</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">10. Haklarınız</h2>
                <p>KVKK kapsamında aşağıdaki haklara sahipsiniz:</p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
                  <li>İşlenmişse buna ilişkin bilgi talep etme</li>
                  <li>İşlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme</li>
                  <li>Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme</li>
                  <li>Eksik veya yanlış işlenmişse düzeltilmesini isteme</li>
                  <li>KVKK'da öngörülen şartlar çerçevesinde silinmesini veya yok edilmesini isteme</li>
                  <li>Düzeltme, silme, yok edilme işlemlerinin, kişisel verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme</li>
                  <li>İşlenen verilerin münhasıran otomatik sistemler ile analiz edilmesi suretiyle kişinin kendisi aleyhine bir sonucun ortaya çıkmasına itiraz etme</li>
                  <li>Kişisel verilerin kanuna aykırı olarak işlenmesi sebebiyle zarara uğraması halinde zararın giderilmesini talep etme</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">11. Başvuru Yöntemi</h2>
                <p>
                  Yukarıda belirtilen haklarınızı kullanmak için, kimliğinizi tespit edici belgelerle birlikte 
                  <strong> info@lakverse.com</strong> adresine e-posta gönderebilir veya yazılı olarak başvurabilirsiniz.
                </p>
                <p className="mt-4">
                  Başvurunuz, talebinizin niteliğine göre en geç 30 (otuz) gün içinde sonuçlandırılacaktır.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">12. Değişiklikler</h2>
                <p>
                  Bu Gizlilik Politikası, yasal düzenlemelerdeki değişiklikler veya iş süreçlerimizdeki güncellemeler 
                  nedeniyle değiştirilebilir. Önemli değişiklikler web sitemizde duyurulacaktır.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-mono">13. İletişim</h2>
                <p>
                  Gizlilik politikamız hakkında sorularınız için bizimle iletişime geçebilirsiniz:
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm sm:text-base">
                  <p className="font-semibold mb-2">LAK VERSE</p>
                  <p className="text-sm text-slate-600">
                    E-posta: info@lakverse.com<br />
                    Web: www.lakverse.com
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
      <CTA locale={currentLocale} />
    </>
  )
}
